import { useState, useMemo } from "react";
import { products, categories } from "@/data/products";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import { Search } from "lucide-react";

export default function Products() {
  const [active, setActive] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const list = useMemo(() => {
    let l = active === "all" ? products : products.filter(p => p.category === active);
    if (q.trim()) {
      const t = q.toLowerCase();
      l = l.filter(p => p.name.toLowerCase().includes(t) || p.tagline.toLowerCase().includes(t));
    }
    return l;
  }, [active, q]);

  return (
    <section className="py-12 md:py-16" data-testid="products-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs tracking-[0.2em] uppercase text-holiday-red font-bold">Our Catalogue</p>
        <h1 className="font-display text-5xl md:text-7xl mt-3 leading-[0.95]">All flavours.<br/><span className="text-holiday-red italic font-serif">All sizes.</span></h1>

        {/* Filters */}
        <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="catalogue-filter-row">
          <button onClick={() => setActive("all")} data-testid="filter-all" className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${active === "all" ? "bg-choc text-cream" : "bg-white border border-choc/15 hover:bg-ice-pink"}`}>All</button>
          {categories.map(c => (
            <button key={c.id} onClick={() => setActive(c.id)} data-testid={`filter-${c.id}`} className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${active === c.id ? "bg-choc text-cream" : "bg-white border border-choc/15 hover:bg-ice-pink"}`}>
              {c.label} <span className="opacity-60 ml-1 text-xs">{c.priceHint}</span>
            </button>
          ))}
          <div className="ml-auto w-full sm:w-auto relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-choc/50"/>
            <input
              value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a flavour"
              className="bg-white border border-choc/15 rounded-full pl-10 pr-4 py-2.5 text-sm w-full sm:w-72 focus:outline-none focus:border-holiday-red"
              data-testid="catalogue-search-input"
            />
          </div>
        </div>

        {/* Sections by category if 'all' */}
        {active === "all" ? (
          categories.map(c => {
            const items = products.filter(p => p.category === c.id && (q.trim() ? (p.name.toLowerCase().includes(q.toLowerCase()) || p.tagline.toLowerCase().includes(q.toLowerCase())) : true));
            if (!items.length) return null;
            return (
              <div key={c.id} className="mt-16" data-testid={`category-section-${c.id}`}>
                <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-holiday-red">{c.priceHint}</p>
                    <h2 className="font-display text-4xl mt-1">{c.label}</h2>
                    <p className="font-serif italic text-choc/70 mt-1">{c.tagline}</p>
                  </div>
                  {c.id === "bulk" && (
                    <span className="sticker bg-holiday-red text-white animate-wobble">Discount on Bulk Orders</span>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {items.map(p => <ProductCard key={p.id} product={p} onOpen={setOpen}/>)}
                </div>
              </div>
            );
          })
        ) : (
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {list.length ? list.map(p => <ProductCard key={p.id} product={p} onOpen={setOpen}/>) : (
              <p className="col-span-full text-center text-choc/60 py-10">No flavours match "{q}". Try another search.</p>
            )}
          </div>
        )}
      </div>

      <ProductModal product={open} onClose={() => setOpen(null)} />
    </section>
  );
}

